import {
  collection,
  doc,
  addDoc,
  getDocs,
  updateDoc,
  deleteDoc,
  query,
  where,
  Timestamp,
  serverTimestamp
} from 'firebase/firestore';
import { auth, db } from '../firebase';
import { automationService } from './automationService';
import { contactService } from './contactService';

export interface ScheduledTrigger {
  id?: string;
  /** Owner — required for Firestore rules and scoped scheduler queries */
  userId: string;
  automationId: string;
  type: 'one_time' | 'recurring' | 'anniversary';
  scheduleConfig: {
    // One-time schedule
    executeAt?: Date;
    
    // Recurring schedule
    frequency?: 'daily' | 'weekly' | 'monthly' | 'yearly';
    interval?: number; // e.g., every 2 weeks
    daysOfWeek?: number[]; // 0-6, Sunday = 0
    daysOfMonth?: number[]; // 1-31
    monthsOfYear?: number[]; // 1-12
    timeOfDay?: string; // HH:MM format
    
    // Anniversary schedule
    anniversaryField?: string; // Contact field containing the date
    daysBefore?: number;
    daysAfter?: number;
    
    // Common
    timezone?: string;
    startDate?: Date;
    endDate?: Date;
  };
  targetCriteria?: {
    segmentIds?: string[];
    tagIds?: string[];
    contactFields?: { field: string; operator: string; value: any }[];
  };
  status: 'active' | 'paused' | 'completed';
  lastExecutedAt?: Timestamp;
  nextExecutionAt?: Timestamp;
  executionCount: number;
  createdAt: Timestamp;
  updatedAt: Timestamp;
}

class AutomationSchedulerService {
  private schedulerInterval: NodeJS.Timeout | null = null;
  private isRunning = false;

  // Start the scheduler
  async startScheduler(): Promise<void> {
    if (this.isRunning) {
      console.log('Scheduler is already running');
      return;
    }

    console.log('Starting Automation Scheduler...');
    this.isRunning = true;

    // Check for scheduled triggers every minute
    this.schedulerInterval = setInterval(async () => {
      await this.processScheduledTriggers();
    }, 60000); // 60 seconds

    // Run immediately on start
    await this.processScheduledTriggers();

    console.log('Automation Scheduler started');
  }

  // Stop the scheduler
  stopScheduler(): void {
    if (this.schedulerInterval) {
      clearInterval(this.schedulerInterval);
      this.schedulerInterval = null;
      this.isRunning = false;
      console.log('Automation Scheduler stopped');
    }
  }

  // Create a scheduled trigger
  async createScheduledTrigger(trigger: Omit<ScheduledTrigger, 'id' | 'createdAt' | 'updatedAt' | 'executionCount' | 'lastExecutedAt' | 'nextExecutionAt'>): Promise<string> {
    try {
      const nextExecution = this.calculateNextExecution(trigger.scheduleConfig);
      
      const triggerData = {
        ...trigger,
        executionCount: 0,
        nextExecutionAt: nextExecution ? Timestamp.fromDate(nextExecution) : null,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };

      const docRef = await addDoc(collection(db, 'scheduledTriggers'), triggerData);
      return docRef.id;
    } catch (error) {
      console.error('Error creating scheduled trigger:', error);
      throw new Error('Failed to create scheduled trigger');
    }
  }

  // Update scheduled trigger
  async updateScheduledTrigger(triggerId: string, updates: Partial<ScheduledTrigger>): Promise<void> {
    try {
      const docRef = doc(db, 'scheduledTriggers', triggerId);
      
      // Recalculate next execution if schedule changed
      if (updates.scheduleConfig) {
        const nextExecution = this.calculateNextExecution(updates.scheduleConfig);
        updates.nextExecutionAt = nextExecution ? Timestamp.fromDate(nextExecution) : undefined;
      }

      await updateDoc(docRef, {
        ...updates,
        updatedAt: serverTimestamp()
      });
    } catch (error) {
      console.error('Error updating scheduled trigger:', error);
      throw new Error('Failed to update scheduled trigger');
    }
  }

  // Delete scheduled trigger
  async deleteScheduledTrigger(triggerId: string): Promise<void> {
    try {
      await deleteDoc(doc(db, 'scheduledTriggers', triggerId));
    } catch (error) {
      console.error('Error deleting scheduled trigger:', error);
      throw new Error('Failed to delete scheduled trigger');
    }
  }

  // Process scheduled triggers
  private async processScheduledTriggers(): Promise<void> {
    try {
      const uid = auth.currentUser?.uid;
      if (!uid) {
        return;
      }

      const now = new Date();
      
      // Only this user's triggers (required by security rules)
      const triggersQuery = query(
        collection(db, 'scheduledTriggers'),
        where('userId', '==', uid),
        where('status', '==', 'active'),
        where('nextExecutionAt', '<=', Timestamp.fromDate(now))
      );

      const snapshot = await getDocs(triggersQuery);
      
      console.log(`Processing ${snapshot.size} scheduled triggers...`);

      for (const docSnap of snapshot.docs) {
        const trigger = { id: docSnap.id, ...docSnap.data() } as ScheduledTrigger;
        await this.executeTrigger(trigger);
      }
    } catch (error) {
      console.error('Error processing scheduled triggers:', error);
    }
  }

  // Execute a scheduled trigger
  private async executeTrigger(trigger: ScheduledTrigger): Promise<void> {
    try {
      console.log(`Executing scheduled trigger ${trigger.id} for automation ${trigger.automationId}`);

      // Get target contacts
      const contacts = await this.getTargetContacts(trigger);
      
      console.log(`Found ${contacts.length} contacts for trigger ${trigger.id}`);

      // Execute automation for each contact
      for (const contact of contacts) {
        try {
          await automationService.triggerAutomation(
            trigger.automationId,
            contact.id,
            {
              triggerType: 'scheduled',
              scheduledTriggerId: trigger.id,
              scheduledAt: new Date()
            }
          );
        } catch (error) {
          console.error(`Error executing automation for contact ${contact.id}:`, error);
        }
      }

      // Update trigger
      const nextExecution = this.calculateNextExecution(trigger.scheduleConfig, new Date());
      const updates: any = {
        lastExecutedAt: serverTimestamp(),
        executionCount: trigger.executionCount + 1,
        updatedAt: serverTimestamp()
      };

      if (nextExecution) {
        updates.nextExecutionAt = Timestamp.fromDate(nextExecution);
      } else {
        // No more executions scheduled
        updates.status = 'completed';
        updates.nextExecutionAt = null;
      }

      await updateDoc(doc(db, 'scheduledTriggers', trigger.id!), updates);

      console.log(`Completed execution of trigger ${trigger.id}`);
    } catch (error) {
      console.error(`Error executing trigger ${trigger.id}:`, error);
    }
  }

  // Get contacts matching target criteria
  private async getTargetContacts(trigger: ScheduledTrigger): Promise<any[]> {
    try {
      if (trigger.type === 'anniversary') {
        return await this.getAnniversaryContacts(trigger);
      }

      // For regular scheduled triggers, get contacts based on criteria
      if (!trigger.targetCriteria) {
        return []; // No criteria means no contacts
      }

      // Get automation to get workspaceId
      const automation = await automationService.getAutomation(trigger.automationId);
      if (!automation || !automation.workspaceId) {
        console.warn(`Automation ${trigger.automationId} missing workspaceId, skipping target contacts`);
        return [];
      }

      const workspaceId = automation.workspaceId;

      // Get all contacts for this workspace (in a real implementation, this should be paginated)
      const contactsQuery = query(
        collection(db, 'contacts'),
        where('workspaceId', '==', workspaceId)
      );
      const contactsSnapshot = await getDocs(contactsQuery);
      let contacts: any[] = contactsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

      // Filter by segments
      if (trigger.targetCriteria.segmentIds && trigger.targetCriteria.segmentIds.length > 0) {
        contacts = contacts.filter(contact =>
          contact.segments?.some((seg: string) => trigger.targetCriteria!.segmentIds!.includes(seg))
        );
      }

      // Filter by tags
      if (trigger.targetCriteria.tagIds && trigger.targetCriteria.tagIds.length > 0) {
        contacts = contacts.filter(contact =>
          contact.tags?.some((tag: string) => trigger.targetCriteria!.tagIds!.includes(tag))
        );
      }

      // Filter by custom fields
      if (trigger.targetCriteria.contactFields) {
        for (const criteria of trigger.targetCriteria.contactFields) {
          contacts = contacts.filter(contact => {
            const value = contact[criteria.field];
            return this.matchesCriteria(value, criteria.operator, criteria.value);
          });
        }
      }

      return contacts;
    } catch (error) {
      console.error('Error getting target contacts:', error);
      return [];
    }
  }

  // Get contacts for anniversary triggers
  private async getAnniversaryContacts(trigger: ScheduledTrigger): Promise<any[]> {
    try {
      const { anniversaryField, daysBefore, daysAfter } = trigger.scheduleConfig;
      
      if (!anniversaryField) return [];

      // Get automation to get workspaceId
      const automation = await automationService.getAutomation(trigger.automationId);
      if (!automation || !automation.workspaceId) {
        console.warn(`Automation ${trigger.automationId} missing workspaceId, skipping anniversary contacts`);
        return [];
      }

      const workspaceId = automation.workspaceId;

      // Filter contacts by workspace
      const contactsQuery = query(
        collection(db, 'contacts'),
        where('workspaceId', '==', workspaceId)
      );
      const contactsSnapshot = await getDocs(contactsQuery);
      const contacts: any[] = contactsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

      const today = new Date();
      const matchingContacts = contacts.filter(contact => {
        const anniversaryValue = contact[anniversaryField];
        if (!anniversaryValue) return false;

        const anniversaryDate = anniversaryValue instanceof Date 
          ? anniversaryValue 
          : new Date(anniversaryValue);

        // Check if anniversary is within the trigger window
        const thisYearAnniversary = new Date(
          today.getFullYear(),
          anniversaryDate.getMonth(),
          anniversaryDate.getDate()
        );

        const daysUntil = Math.floor(
          (thisYearAnniversary.getTime() - today.getTime()) / (1000 * 60 * 60 * 24)
        );

        const beforeDays = daysBefore || 0;
        const afterDays = daysAfter || 0;

        return daysUntil >= -afterDays && daysUntil <= beforeDays;
      });

      return matchingContacts;
    } catch (error) {
      console.error('Error getting anniversary contacts:', error);
      return [];
    }
  }

  // Match field value against criteria
  private matchesCriteria(value: any, operator: string, targetValue: any): boolean {
    switch (operator) {
      case 'equals':
        return value === targetValue;
      case 'not_equals':
        return value !== targetValue;
      case 'contains':
        return String(value).includes(String(targetValue));
      case 'not_contains':
        return !String(value).includes(String(targetValue));
      case 'greater_than':
        return Number(value) > Number(targetValue);
      case 'less_than':
        return Number(value) < Number(targetValue);
      case 'is_empty':
        return !value || value === '';
      case 'is_not_empty':
        return value && value !== '';
      default:
        return false;
    }
  }

  // Calculate next execution time
  private calculateNextExecution(config: ScheduledTrigger['scheduleConfig'], fromDate?: Date): Date | null {
    const now = fromDate || new Date();

    // One-time schedule
    if (config.executeAt) {
      const executeAt = config.executeAt instanceof Date ? config.executeAt : new Date(config.executeAt);
      if (executeAt > now) {
        return executeAt;
      }
      return null; // Already executed
    }

    // Check end date
    if (config.endDate) {
      const endDate = config.endDate instanceof Date ? config.endDate : new Date(config.endDate);
      if (now >= endDate) {
        return null;
      }
    }

    // Recurring schedule
    if (config.frequency) {
      return this.calculateRecurringExecution(config, now);
    }

    // Anniversary schedule - next check is tomorrow
    if (config.anniversaryField) {
      const tomorrow = new Date(now);
      tomorrow.setDate(tomorrow.getDate() + 1);
      tomorrow.setHours(9, 0, 0, 0); // Check at 9 AM
      return tomorrow;
    }

    return null;
  }

  // Calculate recurring execution
  private calculateRecurringExecution(config: ScheduledTrigger['scheduleConfig'], fromDate: Date): Date | null {
    const { frequency, interval = 1, daysOfWeek, daysOfMonth, timeOfDay = '09:00' } = config;
    const [hours, minutes] = timeOfDay.split(':').map(Number);

    let nextDate = new Date(fromDate);
    nextDate.setHours(hours, minutes, 0, 0);

    // If time has passed today, start from tomorrow
    if (nextDate <= fromDate) {
      nextDate.setDate(nextDate.getDate() + 1);
    }

    switch (frequency) {
      case 'daily':
        // Already set to tomorrow if needed
        break;

      case 'weekly':
        if (daysOfWeek && daysOfWeek.length > 0) {
          // Find next matching day of week
          while (!daysOfWeek.includes(nextDate.getDay())) {
            nextDate.setDate(nextDate.getDate() + 1);
          }
        }
        break;

      case 'monthly':
        if (daysOfMonth && daysOfMonth.length > 0) {
          // Find next matching day of month
          while (!daysOfMonth.includes(nextDate.getDate())) {
            nextDate.setDate(nextDate.getDate() + 1);
          }
        }
        break;

      case 'yearly':
        // Add one year
        nextDate.setFullYear(nextDate.getFullYear() + interval);
        break;
    }

    // Apply interval for daily/weekly/monthly
    if (frequency === 'daily' && interval > 1) {
      nextDate.setDate(nextDate.getDate() + (interval - 1));
    } else if (frequency === 'weekly' && interval > 1) {
      nextDate.setDate(nextDate.getDate() + (7 * (interval - 1)));
    } else if (frequency === 'monthly' && interval > 1) {
      nextDate.setMonth(nextDate.getMonth() + (interval - 1));
    }

    return nextDate;
  }

  // Get scheduled triggers for an automation
  async getTriggersForAutomation(automationId: string, userId: string): Promise<ScheduledTrigger[]> {
    try {
      const triggersQuery = query(
        collection(db, 'scheduledTriggers'),
        where('userId', '==', userId),
        where('automationId', '==', automationId)
      );

      const snapshot = await getDocs(triggersQuery);
      return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as ScheduledTrigger));
    } catch (error) {
      console.error('Error getting triggers:', error);
      return [];
    }
  }

  // Pause scheduled trigger
  async pauseTrigger(triggerId: string): Promise<void> {
    await this.updateScheduledTrigger(triggerId, { status: 'paused' });
  }

  // Resume scheduled trigger
  async resumeTrigger(triggerId: string): Promise<void> {
    await this.updateScheduledTrigger(triggerId, { status: 'active' });
  }
}

export const automationScheduler = new AutomationSchedulerService();

/**
 * No-op. Scheduled triggers are processed by Cloud Functions.
 *
 * This used to start a 60-second `setInterval` in the browser that queried
 * `scheduledTriggers` and fanned out an automation per matching contact. Three
 * problems with that:
 *
 *  1. It duplicated `checkScheduledAutomations`, a Cloud Function already
 *     running the identical query on the same one-minute cadence, so every due
 *     trigger was processed twice.
 *  2. There was no locking or leasing, and `nextExecutionAt` was only advanced
 *     *after* the whole contact fan-out finished — so the race window was as long
 *     as the fan-out, and every open tab of the same user ran its own copy.
 *  3. `getTargetContacts` read every contact in the workspace, unpaginated, on
 *     every tick.
 *
 * The scheduler methods on `automationScheduler` are still used by the UI to
 * create, update, pause and resume triggers. Only the client-side polling is
 * gone. The cleanup function is still returned so callers
 * (AutomationSchedulerInitializer) need no change.
 */
export const startAutomationScheduler = (): (() => void) => {
  return () => {};
};

